<template lang="pug">
y-dialog(v-if="showDialog" v-model="showDialog" persistent max-width=550 content-class="round-2xl")
  v-card
    v-card-title.px-4
      v-spacer
      .text-xl.font-bold Change status
      v-spacer
      v-btn(icon color="neutral700" @click="closeDialog")
        v-icon mdi-close
    v-divider
    div.py-6.px-10
      .text-lg.font-semibold To what status do you want to change it?
      .text-md.mb-4 Choose one and click "Confirm"
      template(v-for="selection in selectionItems")
        v-card.pa-4.rounded-lg.mb-3(:key="selection.title" :color="isStatusDisabled(selection.id) ? 'neutral300' : ''" :class="[{ 'selected': isSelected(selection), 'hover-bg': !isStatusDisabled(selection.id), 'cursor-default': isStatusDisabled(selection.id) }]" outlined @click="handleSelectedItem(selection)")
          .d-flex.align-center.mb-1
            v-icon.mr-2(size="17" :color="selection.iconColor") {{ selection.icon }}
            .text-md.font-semibold {{ selection.title }}
          .text-sm.neutral500--text {{ selection.description }}
    v-divider
    v-card-actions.px-6
      v-spacer
      v-btn.button-md.neutral700--text.px-0.underline(text @click="closeDialog") {{ $t('cancel') }}
      v-btn.button-md.neutral700.white--text(:loading="isLoading" depressed :disabled="!selectedItem" @click="handleConfirm") {{ $t('confirm') }}
</template>
  
<script>
import { mapGetters, mapActions } from 'vuex'

export default {
  props: {
    open: {
      type: Boolean,
    },
    parentId: {
      type: Number,
      default: null
    },
    parentData: {
      type: Object,
      required: true
    },
    selectedDocument: {
      type: Object,
      required: true
    },
    isCreator: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      isLoading: false,
      selectionItems: [
        {
          id: 'draft',
          title: 'Draft',
          icon: '$yzi-file-05',
          iconColor: 'orange',
          description: "The document isn't ready yet."
        },
        {
          id: 'created',
          title: 'Created',
          icon: 'mdi-check-bold',
          iconColor: 'green',
          description: 'The document is ready to be sent. No updates and edits available.'
        },
        {
          id: 'requested',
          title: 'Pending approval',
          icon: '$yzi-clock',
          iconColor: 'warning500',
          description: 'The document is ready and available for updates and edits.'
        },
        {
          id: 'approved',
          title: 'Approved',
          icon: 'mdi-check-bold',
          iconColor: 'green',
          description: 'The document is ready and available for updates and edits.'
        },
        {
          id: 'rejected',
          title: 'Rejected',
          icon: 'mdi-close-thick',
          iconColor: 'danger500',
          description: 'The document is ready and available for updates and edits.'
        },
        {
          id: 'active',
          title: 'Active',
          icon: 'mdi-check-bold',
          iconColor: 'green',
          description: 'The document is ready and available for updates and edits.'
        },
        {
          id: 'completed',
          title: 'Completed',
          icon: 'mdi-check-bold',
          iconColor: 'blue',
          description: 'The document is completed. No updates and edits available.'
        },
        {
          id: 'archived',
          title: 'Archived',
          icon: '$yzi-archive',
          iconColor: 'red',
          description: "Document won't be editable unless retrieved."
        },
      ],
      selectedItem: null
    }
  },
  mounted () {
    if (this.selectedDocument.id === 'quotation') {
      this.selectionItems = this.selectionItems.filter(item => !['active', 'completed', 'requested'].includes(item.id))
    } else if (this.selectedDocument.id === 'stock-transfer') {
      const allowed = this.isCreator
        ? ['draft', 'requested', 'completed', 'archived']
        : ['approved', 'rejected', 'archived']
      this.selectionItems = this.selectionItems.filter(item => allowed.includes(item.id))
    } else {
      this.selectionItems = this.selectionItems.filter(item => !['created', 'requested', 'approved', 'rejected'].includes(item.id))
    }
  },
  computed: {
    ...mapGetters({
      company: 'getCompany'
    }),
    showDialog: {
      get() {
        return this.open
      },
      set(newValue) {
        this.$emit('update:open', newValue)
      }
    },
    status () {
      return this.parentData?.request_status || this.parentData?.order_status
    },
    disabledStatuses () {
      if (this.selectedDocument.id === 'stock-transfer') {
        if (this.isCreator) {
          if (this.status === 'draft') return ['draft', 'completed']
          if (this.status === 'requested') return ['requested', 'completed']
          if (this.status === 'approved' || this.status === 'active') return ['draft', 'requested']
          if (this.status === 'rejected') return ['requested', 'completed']
          return ['draft', 'requested', 'completed', 'archived']
        }
        // Non-creator can approve/reject/archive when pending approval
        if (this.status === 'requested') return []
        return ['approved', 'rejected', 'archived']
      }
      if (this.status === 'created') return ['created']
      if (this.status === 'archived') return ['draft', 'created', 'active', 'completed']
      if (this.status === 'draft') return ['draft', 'archived']
      return ['draft', this.status]
    }
  },
  methods: {
    ...mapActions([
      'showMessage'
    ]),
    closeDialog() {
      this.$emit('closed')
      this.showDialog = false
    },
    handleSelectedItem (selection) {
      if (this.isStatusDisabled(selection.id)) return
      this.selectedItem = selection
    },
    isSelected (selection) {
      return this.selectedItem?.id === selection.id
    },
    getEndpoint () {
      const baseEndpoint = `/purchases/${this.company.slug}`
      switch (this.selectedDocument.id) {
        case 'quotation':
          return `${baseEndpoint}/quotations`

        case 'purchase-order':
          return `${baseEndpoint}/orders`

        case 'invoice':
          return `${baseEndpoint}/invoices`

        case 'stock-transfer':
          return `${baseEndpoint}/stock-transfers`

        default:
          return ''
      }
    },
    async handleConfirm () {
      if (this.validateInvoiceCase()) {
        this.$emit('invoiceStatusChanged', this.selectedItem.id)
        return
      }
      try {
        this.isLoading = true
        const endpoint = `${this.getEndpoint()}/${this.parentId}`
        const payload = this.selectedDocument.id === 'quotation' ? { request_status: this.selectedItem.id } : { order_status: this.selectedItem.id }
        const resp = await this.axios.patch(endpoint, payload)
        this.$emit('updated')
        this.updateActivityLog(endpoint, payload, resp, 'PATCH', this.parentId, this.selectedItem.id)
        this.showMessage(['success', 'Status updated successfully'])
        this.closeDialog()
      } catch (error) {
        this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
      } finally {
        this.isLoading = false
      }
    },
    validateInvoiceCase () {
      // Special case to handle on invoice status change
      // Draft -> Active / Completed
      // Active -> Completed
      return this.selectedDocument.id === 'invoice' && (this.status === 'draft' || this.status === 'active') && (this.selectedItem.id === 'active' || this.selectedItem.id === 'completed')
    },
    updateActivityLog (endpoint, payload, response, method, id, objectRef) {
      let data = {
        company: this.company.id,
        object_id: id,
        object_ref: objectRef,
        activity: `${this.selectedDocument.id} status change`,
        raw_data: { endpoint, payload, response, method }
      }
      this.$axios.post('/users/logs', data)
        .catch(() => {})
    },
    isStatusDisabled (status) {
      return this.disabledStatuses.includes(status)
    }
  },
}
</script>

<style scoped>
.selected {
  border-color: var(--v-info500);
}
.cursor-default {
  cursor: default;
}
.v-card--link:before {
  background: none;
}
</style>